import React, { useCallback, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import _, { isEmpty } from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import uuid from 'react-uuid';
import InputReactSelect from 'modules/common/components/InputReactSelect';
import DateInputPicker from 'modules/common/components/DateInputPicker';
import CollapsibleDrawer from 'modules/common/components/CollapsibleDrawer';
import CheckIcon from '@material-ui/icons/Check';
import { getJson } from 'session';
import Pagination from 'modules/common/components/Pagination';
import * as actions from '../actions';
import * as c from '../constants';

const INIT_STATE = {
  report_type: '',
  module: '',
  module_type: '',
  payment_type: '',
  transaction_start_date: '',
  transaction_end_date: '',
};

const AgencyReports = ({
  generateAgencyReports,
  getAgencyReportList,
  agencyReportList,
  agencyReportListPagination,
  isLoading,
  getReportFilter,
  reportFilter,
  isLoadingFilter,
  getReportTypes,
  reportTypes,
  isLoadingReportTypes,
  getReportAccess,
}) => {
  const [state, setState] = React.useState(INIT_STATE);
  const [refreshToggle, setRefreshToggle] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [rightDrawerOpen, setRightDrawerOpen] = React.useState(false);
  const authProfile = getJson('profile') || {};
  const email = _.get(authProfile, 'email', '');

  const reportTypeOptions = useMemo(() => {
    if (isEmpty(reportTypes)) return [];
    return Object.entries(reportTypes).map(([value, label]) => ({
      label,
      value,
    }));
  }, [reportTypes]);

  useEffect(() => {
    getReportTypes();
  }, []);

  useEffect(() => {
    const query = { page, per_page: 20 };
    getAgencyReportList(query);
  }, [refreshToggle, page]);

  useEffect(() => {
    if (state.report_type) {
      getReportFilter({ report_type: state.report_type });
    }
  }, [state.report_type]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
      ...(name === 'module' && { module_type: '' }),
    }));
  };

  const handleOnChangeDate = (key) => (e) => {
    setState((prevState) => ({
      ...prevState,
      [key]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    generateAgencyReports(state, () => {
      setState(INIT_STATE);
      setRefreshToggle((prev) => !prev);
      setRightDrawerOpen((prev) => !prev);
    });
  };

  const handleDownload = (id) => (e) => {
    e.preventDefault();
    const reportAccessArgs = {
      path: `/api/v1/administrators/report_file_exports_download/${id}`,
      query: {
        email,
        output: 'D',
      },
    };
    getReportAccess(reportAccessArgs, (response) => {
      const src = `${response?.res?.res?.url || ''}`;
      window.location.href = src;
    });
  };

  const formatLabel = (string) => {
    if (!string) return '';
    return string
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const optionsSort = (a, b) => {
    // Priority labels to appear first
    const priorityLabels = ['ALL'];
    const aIsPriority = priorityLabels.includes(a.label);
    const bIsPriority = priorityLabels.includes(b.label);

    if (aIsPriority && !bIsPriority) return -1;
    if (!aIsPriority && bIsPriority) return 1;

    return a.label.localeCompare(b.label);
  };

  const badgeIcon = useCallback(
    (isRequired, key) => {
      if (isRequired && !state[key]) {
        return (
          <span className="badge badge-danger badge-circle badge-sm">
            Required
          </span>
        );
      }
      return (
        <CheckIcon
          className="text-success font-weight-bold"
          style={{ fontSize: 15 }}
        />
      );
    },
    [state]
  );

  const getLabel = (key, isRequired) => {
    const label = formatLabel(key);
    const icon = badgeIcon(isRequired, key);
    return (
      <div className="d-flex align-items-center mb-1" style={{ gap: '.3rem' }}>
        <small className="text-muted text-md">{label}</small>
        {icon}
      </div>
    );
  };

  const renderFilterSection = () => {
    if (isLoadingFilter) {
      return (
        <div
          className="d-flex flex-column w-full py-3"
          style={{ minWidth: '30%', flexGrow: 1, gap: '10px' }}
        >
          <p className="text-muted font-italic text-sm text-center w-100">
            Loading...
          </p>
        </div>
      );
    }

    if (!isEmpty(reportFilter)) {
      const filters = _.get(reportFilter, 'filters', {});

      return (
        <div
          className="d-flex flex-column w-full py-3"
          style={{ minWidth: '30%', flexGrow: 1, gap: '10px' }}
        >
          {Object.keys(filters).map((key) => {
            const obj = filters[key];
            const type = _.get(obj, 'type');
            const isRequired = _.get(obj, 'validation') === 'required';
            const fieldLabel = getLabel(key, isRequired);

            if (type === 'options') {
              const choices = _.get(obj, 'choices');
              const options = Object.keys(choices)
                .map((choice) => ({
                  label: String(choices[choice]),
                  value: choice,
                }))
                .sort(optionsSort);
              const moduleBaseChoices = choices?.[state.module] || {};
              const moduleBaseOptions = Object.keys(moduleBaseChoices)
                .map((choice) => ({
                  label: String(moduleBaseChoices[choice]),
                  value: choice,
                }))
                .sort(optionsSort);
              const isModuleType = key === 'module_type';
              const isSourceType = key === 'source_type';
              const isTransactionStatus = key === 'transaction_status';
              const isFieldDisabled =
                (isModuleType || isSourceType || isTransactionStatus) &&
                !state.module;
              const isDisabled = !state.report_type || isFieldDisabled;
              const isModuleBase =
                isModuleType || isSourceType || isTransactionStatus;
              return (
                <label
                  key={uuid()}
                  htmlFor={key}
                  className="d-flex flex-column mb-0"
                >
                  {fieldLabel}
                  <InputReactSelect
                    name={key}
                    id={key}
                    options={isModuleBase ? moduleBaseOptions : options}
                    isClearable
                    value={(isModuleBase ? moduleBaseOptions : options).find(
                      (item) => item.value === state[key]
                    )}
                    onChange={handleOnChange}
                    isDisabled={isDisabled}
                  />
                </label>
              );
            }

            if (type === 'date') {
              const dateFormat = _.get(obj, 'format');
              return (
                <label
                  key={uuid()}
                  htmlFor={key}
                  className="d-flex flex-column mb-0"
                >
                  {fieldLabel}
                  <DateInputPicker
                    name={key}
                    value={state[key]}
                    onChange={handleOnChangeDate(key)}
                    disabled={!state.report_type}
                    isRequired={isRequired}
                    dateFormat={dateFormat}
                    popperPlacement="left-start"
                  />
                </label>
              );
            }

            return (
              <label
                key={uuid()}
                htmlFor={key}
                className="d-flex flex-column mb-0"
              >
                {fieldLabel}
                <input
                  required={isRequired}
                  type="text"
                  name={key}
                  id={key}
                  className="form-control"
                  value={state[key]}
                  onChange={handleOnChange}
                  disabled={!state.report_type}
                />
              </label>
            );
          })}

          <button
            className="btn btn-primary mt-3"
            type="submit"
            disabled={!state.report_type}
          >
            Generate Report
          </button>
        </div>
      );
    }

    return (
      <div
        className="d-flex flex-column w-full py-3 pr-3 mr-3"
        style={{ minWidth: '30%', flexGrow: 1, gap: '10px' }}
      >
        <p className="text-muted font-italic text-sm text-center w-100">
          No report filter found. Please select a report first.
        </p>
      </div>
    );
  };

  const renderTbody = () => {
    if (isLoading) {
      return (
        <tr
          className="text-muted font-italic text-sm text-center py-5"
          style={{
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
            height: '100%',
          }}
        >
          <td colSpan={5}>Loading...</td>
        </tr>
      );
    }

    if (isEmpty(agencyReportList)) {
      return (
        <tr
          className="text-muted font-italic text-sm text-center"
          style={{
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <td colSpan={5}>No Report Available...</td>
        </tr>
      );
    }

    return agencyReportList.map((item) => {
      const agencyReport = _.get(item, 'attributes');
      const { date_generated, report_name, start_date, end_date, status } =
        agencyReport;

      return (
        <tr
          key={uuid()}
          style={{
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
          }}
        >
          <td>{date_generated}</td>
          <td>{report_name}</td>
          <td>
            {start_date} - {end_date}
          </td>
          <td>
            {status === 'STARTED EXPORTING' && (
              <span className="badge badge-pill badge-warning">{status}</span>
            )}
            {status === 'FAILED' && (
              <span className="badge badge-pill badge-danger">{status}</span>
            )}
            {status === 'FINISHED EXPORTING' && (
              <span className="badge badge-pill badge-success">{status}</span>
            )}
          </td>
          <td className="text-right">
            {status !== 'FINISHED EXPORTING' ? (
              <button className="btn btn-primary btn-sm" disabled type="button">
                Download
              </button>
            ) : (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleDownload(item.id)}
                type="button"
              >
                Download
              </button>
            )}
          </td>
        </tr>
      );
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="h-100"
      style={{ paddingTop: '5rem', marginTop: '-5rem' }}
    >
      <div
        className="d-flex flex-column align-items-end h-100 p-5"
        style={{ gap: '1rem' }}
      >
        <CollapsibleDrawer
          placement="right"
          isOpen={rightDrawerOpen}
          onToggle={setRightDrawerOpen}
          width="400px"
        >
          <div
            className="d-flex flex-column"
            style={{ height: '100%', minHeight: 0, width: '100%' }}
          >
            <div
              className="p-3"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <h2 className="my-3 text-center font-weight-bold">
                Filter Panel
              </h2>

              <label
                htmlFor="report_type"
                className="d-flex flex-column mb-2"
                style={{ minWidth: '30%' }}
              >
                <small className="text-muted text-md">Report</small>
                <InputReactSelect
                  name="report_type"
                  id="report_type"
                  options={reportTypeOptions}
                  isClearable
                  value={reportTypeOptions.find(
                    (item) => item.value === state.report_type
                  )}
                  onChange={handleOnChange}
                  isLoading={isLoadingReportTypes}
                />
              </label>

              {state.report_type && renderFilterSection()}
            </div>

            <button
              className="w-full border-top mt-auto border-left-0 border-right-0 border-bottom-0 font-weight-bold"
              style={{
                fontSize: '1rem',
                padding: '1.2rem',
                background: 'none',
                flexShrink: 0,
              }}
              type="button"
              onClick={() => setRightDrawerOpen(false)}
            >
              <span role="img" aria-label="Close Icon">
                ❌
              </span>{' '}
              Close
            </button>
          </div>
        </CollapsibleDrawer>

        <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
          <button
            className="btn btn-secondary font-weight-bold text-uppercase"
            style={{ width: '8rem', height: '3rem', fontSize: '1.1rem' }}
            type="button"
            onClick={() => setRefreshToggle((prev) => !prev)}
            disabled={isLoading}
          >
            Refresh
          </button>

          <button
            onClick={() => setRightDrawerOpen(!rightDrawerOpen)}
            type="button"
            className="btn btn-light font-weight-bold"
            style={{ width: '8rem', height: '3rem', fontSize: '1.1rem' }}
          >
            FILTERS
          </button>
        </div>

        <div className="flex-grow-1 d-flex flex-column w-100">
          <table
            className="table table-hover table-bordered mb-0 w-100 h-100"
            style={{ tableLayout: 'fixed' }}
          >
            <thead style={{ display: 'block' }}>
              <tr
                style={{
                  display: 'table',
                  width: '100%',
                  tableLayout: 'fixed',
                }}
              >
                <th scope="col" className="font-weight-semibold">
                  DATE GENERATED
                </th>
                <th scope="col" className="font-weight-semibold">
                  REPORT NAME
                </th>
                <th scope="col" className="font-weight-semibold">
                  START-END DATE
                </th>
                <th scope="col" className="font-weight-semibold">
                  <span className="pr-3">STATUS</span>
                </th>
                <th scope="col" className="font-weight-semibold text-right">
                  <span className="pr-3">ACTION</span>
                </th>
              </tr>
            </thead>
            <tbody
              style={{
                display: 'block',
                height: 'calc(100vh - 340px)',
                overflowY: 'auto',
                scrollbarWidth: 'none' /* Firefox */,
                msOverflowStyle: 'none' /* IE and Edge */,
              }}
              className="hide-scrollbar"
            >
              {renderTbody()}
            </tbody>
            <tfoot style={{ display: 'block' }}>
              <tr
                style={{
                  display: 'table',
                  width: '100%',
                  tableLayout: 'fixed',
                }}
              >
                <td colSpan={5} className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <p className="text-muted font-italic text-sm mb-0">
                      Showing {agencyReportList.length} of{' '}
                      {agencyReportListPagination.total} entries
                    </p>
                    <Pagination
                      data={agencyReportListPagination}
                      onChange={setPage}
                    />
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </form>
  );
};

AgencyReports.defaultProps = {
  reportFilter: {},
  isLoadingFilter: false,
};

AgencyReports.propTypes = {
  generateAgencyReports: PropTypes.instanceOf(Function).isRequired,
  getAgencyReportList: PropTypes.instanceOf(Function).isRequired,
  agencyReportList: PropTypes.instanceOf(Array).isRequired,
  agencyReportListPagination: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getReportFilter: PropTypes.instanceOf(Function).isRequired,
  reportFilter: PropTypes.instanceOf(Object),
  isLoadingFilter: PropTypes.bool,
  getReportTypes: PropTypes.instanceOf(Function).isRequired,
  reportTypes: PropTypes.instanceOf(Object).isRequired,
  isLoadingReportTypes: PropTypes.bool.isRequired,
  getReportAccess: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => {
  return {
    agencyReportList: _.get(api, `${c.AGENCY_REPORT_LIST}.list`) || [],
    agencyReportListPagination:
      _.get(api, `${c.AGENCY_REPORT_LIST}.pager`) || [],
    isLoading: _.get(api, 'loading', []).indexOf(c.AGENCY_REPORT_LIST) > -1,
    reportFilter: _.get(api, `${c.REPORT_FILTER}.res`),
    isLoadingFilter: _.get(api, 'loading', []).indexOf(c.REPORT_FILTER) > -1,
    reportTypes: _.get(api, `${c.REPORT_TYPES}.res.report_types`) || {},
    isLoadingReportTypes:
      _.get(api, 'loading', []).indexOf(c.REPORT_TYPES) > -1,
  };
};

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(AgencyReports);
